/**
 * Analytics + verification wiring for MyMoneyLocal.
 *
 * All IDs come from environment variables — no placeholders in code.
 * When an env var is unset, the corresponding tool is a no-op (safe for local dev).
 *
 *   REACT_APP_GA4_MEASUREMENT_ID    Google Analytics 4 ("G-XXXXXXX")
 *   REACT_APP_CLARITY_ID            Microsoft Clarity project id
 *   REACT_APP_BING_ID               Bing Webmaster meta content value
 *   REACT_APP_GSC_VERIFICATION      Google Search Console meta content value (optional)
 *
 * Legacy variable names (REACT_APP_CLARITY_PROJECT_ID, REACT_APP_BING_VERIFICATION)
 * are still honoured for backwards compatibility.
 */

const GA_ID = process.env.REACT_APP_GA4_MEASUREMENT_ID || "";
const CLARITY_ID =
  process.env.REACT_APP_CLARITY_ID || process.env.REACT_APP_CLARITY_PROJECT_ID || "";
const BING =
  process.env.REACT_APP_BING_ID || process.env.REACT_APP_BING_VERIFICATION || "";

let bootstrapped = false;

function upsertMeta(name, content) {
  if (!content) return;
  let el = document.head.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function injectScript({ id, src, async = true, defer = false, inline }) {
  if (document.getElementById(id)) return;
  const s = document.createElement("script");
  s.id = id;
  if (async) s.async = true;
  if (defer) s.defer = true;
  if (src) s.src = src;
  if (inline) s.text = inline;
  document.head.appendChild(s);
}

/** Loaded once on app start. Adds verification meta tags + GA4 + Clarity + global click listener. */
export function bootstrapAnalytics() {
  if (bootstrapped) return;
  bootstrapped = true;

  // ---- Verification meta tags (Google Search Console lives in public/index.html; Bing here) ----
  if (BING) upsertMeta("msvalidate.01", BING);

  // ---- Google Analytics 4 ----
  if (GA_ID) {
    injectScript({
      id: "ga4-lib",
      src: `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`,
    });
    injectScript({
      id: "ga4-init",
      inline: `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);} window.gtag=gtag; gtag('js', new Date()); gtag('config', '${GA_ID}', { send_page_view: false });`,
    });
  }

  // ---- Microsoft Clarity ----
  if (CLARITY_ID) {
    injectScript({
      id: "clarity-init",
      inline: `(function(c,l,a,r,i,t,y){c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);})(window, document, "clarity", "script", "${CLARITY_ID}");`,
    });
  }

  // ---- Global outbound + download click tracking ----
  // Attached once. Safe when GA is off (trackEvent no-ops).
  document.addEventListener("click", handleGlobalClick, { capture: true });
}

const DOWNLOAD_EXTENSIONS = /\.(pdf|csv|xlsx?|docx?|pptx?|zip|rar|7z|dmg|exe|apk|mp3|mp4|mov|png|jpe?g|svg|json|txt)(\?.*)?$/i;

function handleGlobalClick(e) {
  if (e.defaultPrevented || e.button !== 0) return;
  const a = e.target.closest && e.target.closest("a");
  if (!a || !a.href) return;
  let url;
  try {
    url = new URL(a.href, window.location.href);
  } catch (_) {
    return;
  }
  const isOutbound =
    /^https?:$/.test(url.protocol) && url.hostname && url.hostname !== window.location.hostname;
  const isDownload = a.hasAttribute("download") || DOWNLOAD_EXTENSIONS.test(url.pathname);
  const label = (a.textContent || "").trim().slice(0, 80) || a.href;
  if (isDownload) {
    trackEvent("file_download", {
      file_url: a.href,
      file_extension: (url.pathname.match(/\.([a-z0-9]+)(\?|$)/i) || [])[1] || "",
      link_text: label,
    });
  } else if (isOutbound) {
    trackEvent("click_outbound", {
      link_url: a.href,
      link_domain: url.hostname,
      link_text: label,
    });
    // Canonical GA4 conversion name (additive)
    trackEvent("outbound_link_click", {
      link_url: a.href,
      link_domain: url.hostname,
      link_text: label,
    });
  }
}

/** Send a GA4 event (safe no-op when gtag isn't loaded). */
export function trackEvent(name, params = {}) {
  // Gate on gtag presence only — this lets QA harnesses stub gtag without
  // needing to inject an env var at build time.
  if (typeof window.gtag !== "function") return;
  window.gtag("event", name, params);
}

/** Send a GA4 page_view (called on every route change). */
export function trackPageView(path, title) {
  if (typeof window.gtag !== "function") return;
  window.gtag("event", "page_view", {
    page_location: window.location.origin + path,
    page_path: path,
    page_title: title || document.title,
  });
}

// ---- Domain-specific event helpers (small, purposeful surface) ----
export const analytics = {
  articleView: (slug) => trackEvent("article_view", { article_slug: slug }),
  calculatorUse: (slug, action = "compute") => {
    trackEvent("calculator_use", { calculator_slug: slug, action });
    // Additive canonical GA4 conversion events (dashboards can use either name)
    if (action === "compute") trackEvent("calculator_completed", { calculator_slug: slug });
    if (action === "start") trackEvent("calculator_started", { calculator_slug: slug });
  },
  calculatorStarted: (slug) => trackEvent("calculator_started", { calculator_slug: slug }),
  calculatorCompleted: (slug) => trackEvent("calculator_completed", { calculator_slug: slug }),
  ctaClick: (label, location) => trackEvent("cta_click", { label, location }),
  newsletterClick: (source) => trackEvent("newsletter_click", { source }),
  newsletterSubmit: (source) => {
    trackEvent("newsletter_submit", { source });
    // Canonical GA4 conversion name
    trackEvent("email_signup", { source });
  },
  emailSignup: (source) => trackEvent("email_signup", { source }),
  contactSubmission: (subject) => trackEvent("contact_submission", { subject }),
  affiliateClick: (label, destination) =>
    trackEvent("affiliate_click", { label, destination }),
  siteSearch: (query, results) =>
    trackEvent("site_search", { search_term: query, results_count: results ?? 0 }),
  fileDownload: (url, extension) => trackEvent("file_download", { file_url: url, file_extension: extension }),
  outboundClick: (url, domain) => {
    trackEvent("click_outbound", { link_url: url, link_domain: domain });
    // Canonical GA4 conversion name
    trackEvent("outbound_link_click", { link_url: url, link_domain: domain });
  },
  tryThisNextViewed: (calcSlug, articleSlugs) =>
    trackEvent("try_this_next_viewed", {
      calculator_slug: calcSlug,
      article_slugs: (articleSlugs || []).join(","),
      recommendation_count: (articleSlugs || []).length,
    }),
  tryThisNextClicked: (calcSlug, articleSlug, position) =>
    trackEvent("try_this_next_clicked", {
      calculator_slug: calcSlug,
      article_slug: articleSlug,
      position,
    }),
  calculatorResultRecommendationClicked: (calcSlug, articleSlug, position) =>
    trackEvent("calculator_result_recommendation_clicked", {
      calculator_slug: calcSlug,
      article_slug: articleSlug,
      position,
    }),
  aiRequest: (feature, calcSlug) =>
    trackEvent("ai_request", { feature, calculator_slug: calcSlug }),
  custom: (name, params = {}) => trackEvent(name, params),
};
